/**
 * Enhancement queue (backend: support_queue) — the XS work stream from
 * Freshservice (Applications Support / Awaiting Approval), triaged BY
 * HAND into planner departments and ranked within each. Two homes since
 * 2026-08-14: triage mode (no `department` prop) renders on /helpdesk's
 * Enhancements tab — unrouted work lives in the triage inbox, not on
 * Departments — while each department's working queue stays on its
 * /departments/:slug page. Deliberately separate from project intake
 * (no CFO approval funnel, no exec Gantt).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Check, ChevronRight, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import {
  useSupportQueue,
  useSyncSupportQueue,
  useUpdateSupportQueueItem,
  useSetApprovalOverride,
  usePromoteSupportQueueItem,
  type SupportQueueItem,
} from "@/hooks/useSupportQueue";
import { useCan } from "@/lib/roles";

const FRESHSERVICE_PORTAL = "helpdesk.etereman.com";

// Policy 2026-07-31: every request needs approval. Unapproved items stay
// visible (they're real requests) but out of the working list.
function approvalBadge(item: SupportQueueItem) {
  if (item.approval_override === "approved") {
    // Never imply Freshservice said so — this was a PMO decision.
    return (
      <span
        className="inline-flex shrink-0 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30"
        title={`Approved in the planner${item.approval_override_by ? ` by ${item.approval_override_by}` : ""} — Freshservice has no approval (typically because the requester IS the approver)`}
      >
        Approved (planner)
      </span>
    );
  }
  return approvalStateBadge(item.approval_state);
}

function approvalStateBadge(state: SupportQueueItem["approval_state"]) {
  const map = {
    approved: ["bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30", "Approved"],
    pending: ["bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30", "Approval pending"],
    rejected: ["bg-raised text-ink-muted ring-line", "Rejected"],
    none: ["bg-raised text-ink-muted ring-line", "No approval"],
  } as const;
  const [cls, label] = map[state] ?? map.none;
  return (
    <span className={cn("inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1", cls)}>
      {label}
    </span>
  );
}

// Days waiting since the FS ticket was created — amber at 30+ so stuck
// items surface (same threshold as the Intake age chip).
function AgeChip({ createdAt }: { createdAt: string | null }) {
  if (!createdAt) return null;
  const t0 = Date.parse(createdAt);
  if (Number.isNaN(t0)) return null;
  const age = Math.floor((Date.now() - t0) / 86_400_000);
  if (age < 0) return null;
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
        age >= 30 ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300" : "bg-raised text-ink-muted",
      )}
      title={`Waiting ${age} day${age === 1 ? "" : "s"}`}
    >
      {age}d
    </span>
  );
}

function jiraPill(category: string | null, status: string | null) {
  if (!status) return null;
  const cls =
    category === "done"
      ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-200 dark:ring-emerald-500/30"
      : category === "indeterminate"
        ? "bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-sky-200 dark:ring-sky-500/30"
        : "bg-raised text-ink-3 ring-line";
  return (
    <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1", cls)}>
      {status}
    </span>
  );
}

export function SupportQueueSection({
  departments,
  department,
}: {
  departments: string[];
  /** When set, render THAT department's working queue + closed history.
   *  When omitted, render the triage inbox: awaiting approval and
   *  approved-but-untriaged. Assigned work lives on department pages. */
  department?: string;
}) {
  const { isPmo } = useCan();
  const { data: items = [], isLoading } = useSupportQueue();
  const sync = useSyncSupportQueue();
  const update = useUpdateSupportQueueItem();
  const setApproval = useSetApprovalOverride();
  // A failed Freshservice sync must LOOK like a failure. Without this the
  // section renders "nothing in the queue", which reads as "no tickets"
  // when the truth may be a renamed group or an auth problem.
  const [syncError, setSyncError] = useState<string | null>(null);

  const isDone = (i: SupportQueueItem) =>
    i.is_closed || i.done_manual || i.jira_status_category === "done";

  const scoped = useMemo(
    () =>
      department
        ? items.filter((i) => (i.department ?? "") === department)
        : items.filter((i) => !(i.department ?? "").trim()),
    [items, department],
  );

  const groups = useMemo(() => {
    const notDone = scoped.filter((i) => !isDone(i));
    const done = scoped
      .filter(isDone)
      .sort(
        (a, b) =>
          (a.department ?? "~").localeCompare(b.department ?? "~") ||
          a.fs_ticket_id - b.fs_ticket_id,
      );
    // Only APPROVED work enters the department queues — everything else
    // is parked below, present but not up for prioritisation.
    const open = notDone.filter((i) => i.approval_state_effective === "approved");
    const parked = notDone.filter((i) => i.approval_state_effective !== "approved");
    const byDept = new Map<string, SupportQueueItem[]>();
    for (const i of open) {
      const key = (i.department ?? "").trim() || "__unassigned";
      byDept.set(key, [...(byDept.get(key) ?? []), i]);
    }
    for (const list of byDept.values()) {
      list.sort((a, b) => (a.rank ?? 1e9) - (b.rank ?? 1e9) || a.id - b.id);
    }
    const deptKeys = Array.from(byDept.keys())
      .filter((k) => k !== "__unassigned")
      .sort();
    return { byDept, deptKeys, done, parked };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped]);

  // Routing is the one action whose result is invisible from the triage
  // inbox (the row leaves the page) — the toast says where it went.
  const patchItem = (id: number, fields: Partial<SupportQueueItem>) => {
    const dept = fields.department;
    update.mutate(
      { id, ...fields },
      dept
        ? {
            onSuccess: () =>
              toast.success(
                `Moved to ${dept}'s queue — rank it on the department page`,
              ),
            onError: () => toast.error("Couldn't route the request"),
          }
        : {
            onError: () => toast.error("Couldn't save"),
          },
    );
  };

  const move = (list: SupportQueueItem[], idx: number, dir: -1 | 1) => {
    const other = idx + dir;
    if (other < 0 || other >= list.length) return;
    // Swap ranks; seed 1..n when unranked so the swap has something to hold.
    const ranks = list.map((it, i) => it.rank ?? i + 1);
    update.mutate({ id: list[idx].id, rank: ranks[other] });
    update.mutate({ id: list[other].id, rank: ranks[idx] });
  };

  if (isLoading && items.length === 0) {
    return (
      <section className="rounded-xl border border-line bg-surface p-6">
        <h2 className="text-sm font-semibold text-ink">Enhancement queue</h2>
        <div className="mt-3 h-16 animate-pulse rounded-lg bg-raised" />
      </section>
    );
  }

  const unassigned = groups.byDept.get("__unassigned") ?? [];

  return (
    <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
      <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">
            {department ? `${department} — Enhancement queue` : "Enhancement queue"}{" "}
            <span className="text-ink-subtle">
              ({Array.from(groups.byDept.values()).reduce((n, l) => n + l.length, 0)}{" "}
            approved · {groups.parked.length} awaiting approval)
            </span>
          </h2>
          <p className="text-[11px] text-ink-muted">
            {department
              ? "Approved XS enhancements for this department. Rank them, link the Jira card, and each closes when its card does."
              : "XS enhancement requests from the Service Desk. Approve one, then pick a department — it moves to that department's page."}
          </p>
        </div>
        {!isPmo && (
          <span className="text-[11px] text-ink-subtle" title="Routing, approval and done are PMO actions">Read-only — PMO manages this queue</span>
        )}
        {isPmo && (
        <button
          onClick={() =>
            sync.mutate(undefined, {
              onSuccess: (d: any) => {
                setSyncError(null);
                toast.success(`Synced — ${d.fetched} in Freshservice`);
              },
              onError: (e: any) => {
                const msg = e?.response?.data?.detail ?? "Freshservice sync failed.";
                setSyncError(msg);
                toast.error(msg);
              },
            })
          }
          disabled={sync.isPending}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-ink-3 hover:bg-raised disabled:opacity-50"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", sync.isPending && "animate-spin")} />
          Refresh
        </button>
        )}
      </div>

      {syncError && (
        <div className="border-b border-red-100 dark:border-red-500/30 bg-red-50/60 dark:bg-red-500/10 px-5 py-2.5 text-xs text-red-800 dark:text-red-300">
          <strong>Couldn't reach the Service Desk.</strong> {syncError} — the
          list below may be out of date.
        </div>
      )}

      {/* Flow strip — triage mode only. The lifecycle is invisible from
          any single row (routing makes the row leave this page), so the
          strip states it once, with live counts. */}
      {!department && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-line-soft bg-canvas/60 px-5 py-2 text-[11px] text-ink-muted">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30">
            Awaiting approval · {groups.parked.length}
          </span>
          <ChevronRight className="h-3 w-3 text-ink-faint" />
          <span className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 font-medium text-ink-3 ring-1 ring-line">
            Needs department · {(groups.byDept.get("__unassigned") ?? []).length}
          </span>
          <ChevronRight className="h-3 w-3 text-ink-faint" />
          <Link
            to="/departments"
            className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 font-medium text-brand-navy-600 dark:text-brand-navy-400 ring-1 ring-line hover:ring-brand-navy-300"
            title="Routed requests are ranked on their department's page"
          >
            Department queues
          </Link>
          <ChevronRight className="h-3 w-3 text-ink-faint" />
          <span className="font-medium text-ink-3">Done</span>
          <span className="ml-1 text-ink-subtle">
            — approve, route to a department, then the work is ranked and
            closed from that department's page.
          </span>
        </div>
      )}
      {items.length === 0 && !syncError ? (
        <div className="px-5 py-8 text-center text-xs text-ink-subtle">
          {department
            ? "Nothing assigned to this department yet."
            : "Nothing to triage — every request has a department or is awaiting approval."}
        </div>
      ) : (
        <div className="divide-y divide-line-soft">
          {unassigned.length > 0 && (
            <QueueGroup
              title="Needs department"
              tone="amber"
              items={unassigned}
              departments={departments}
              onPatch={patchItem}
              onMove={(idx, dir) => move(unassigned, idx, dir)}
              onWithdraw={(id) => setApproval.mutate({ id, approved: false })}
            />
          )}
          {groups.deptKeys.map((dept) => (
            <QueueGroup
              key={dept}
              title={dept}
              tone="slate"
              items={groups.byDept.get(dept) ?? []}
              departments={departments}
              onPatch={patchItem}
              onMove={(idx, dir) => move(groups.byDept.get(dept) ?? [], idx, dir)}
              onWithdraw={(id) => setApproval.mutate({ id, approved: false })}
            />
          ))}
          {groups.parked.length > 0 && (
            <div className="bg-canvas/60">
              <div className="px-5 pt-3 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                Not approved yet{" "}
                <span className="text-ink-subtle">({groups.parked.length})</span>
                <span className="ml-2 font-normal normal-case tracking-normal text-ink-subtle">
                  — parked until Freshservice shows an approval; not part of the
                  working queue
                </span>
              </div>
              <ul className="divide-y divide-line-soft px-2 pb-2">
                {groups.parked.map((item) => (
                  <li key={item.id} className="flex items-center gap-3 px-3 py-2 opacity-70">
                    <a
                      href={`https://${FRESHSERVICE_PORTAL}/a/tickets/${item.fs_ticket_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 inline-flex items-center gap-1 font-mono text-[11px] text-sky-700 dark:text-sky-300 hover:underline"
                    >
                      #{item.fs_ticket_id}
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="text-sm text-ink-2">{item.subject}</span>
                      <span className="ml-2 text-[10px] text-ink-subtle">
                        {item.requester ? `by ${item.requester}` : "submitter unknown"}
                        {item.requested_for && ` · for ${item.requested_for}`}
                        {item.fs_department && ` · from ${item.fs_department}`}
                      </span>
                    </span>
                    {item.approver_name && (
                      <span className="shrink-0 text-[10px] text-ink-subtle">
                        with {item.approver_name}
                      </span>
                    )}
                    <AgeChip createdAt={item.fs_created_at} />
                    {approvalStateBadge(item.approval_state)}
                    <button
                      onClick={() =>
                        setApproval.mutate(
                          { id: item.id, approved: true },
                          {
                            onSuccess: () => toast.success("Approved in the planner"),
                            onError: () => toast.error("Couldn't record the approval"),
                          },
                        )
                      }
                      className="shrink-0 rounded-md border border-emerald-200 dark:border-emerald-500/30 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-500/15"
                      title="Record approval here — for requests the approver raised themselves, which Freshservice can never approve"
                    >
                      Approve here
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {groups.done.length > 0 && (
            <details className="px-5 py-3" open={groups.done.length <= 5}>
              <summary className="cursor-pointer text-xs font-medium text-ink-muted">
                Done ({groups.done.length})
                <span className="ml-2 font-normal text-ink-subtle">
                  — completed work stays here by department; Reopen puts it back
                </span>
              </summary>
              <ul className="mt-2 space-y-1">
                {groups.done.map((i) => (
                  <li key={i.id} className="flex items-center gap-2 py-1 text-xs">
                    <Check className="h-3 w-3 shrink-0 text-emerald-500" />
                    <a
                      href={`https://${FRESHSERVICE_PORTAL}/a/tickets/${i.fs_ticket_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="shrink-0 font-mono text-[11px] text-sky-700 dark:text-sky-300 hover:underline"
                    >
                      #{i.fs_ticket_id}
                    </a>
                    <span className="min-w-0 flex-1 truncate text-ink-muted line-through">
                      {i.subject}
                    </span>
                    {/* Department on the row: without it, a finished item is
                        unfindable — you can't tell whose queue it came from. */}
                    <span className="shrink-0 rounded-full bg-raised px-2 py-0.5 text-[10px] font-medium text-ink-muted">
                      {i.department || "No department"}
                    </span>
                    {jiraPill(i.jira_status_category, i.jira_status)}
                    {/* Done was a one-way door until now — the reason a
                        mis-click couldn't be undone from the UI. */}
                    {i.done_manual && (
                      <button
                        onClick={() =>
                          update.mutate(
                            { id: i.id, done_manual: false },
                            {
                              onSuccess: () => toast.success("Reopened"),
                              onError: () => toast.error("Couldn't reopen"),
                            },
                          )
                        }
                        className="shrink-0 rounded-md border border-line px-2 py-0.5 text-[10px] font-semibold text-ink-3 hover:border-brand-navy-300 hover:text-brand-navy-700 dark:hover:text-brand-navy-300"
                        title="Put this back in its department queue"
                      >
                        Reopen
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

function QueueGroup({
  title,
  tone,
  items,
  departments,
  onPatch,
  onMove,
  onWithdraw,
}: {
  title: string;
  tone: "amber" | "slate";
  items: SupportQueueItem[];
  departments: string[];
  onPatch: (id: number, fields: Partial<SupportQueueItem>) => void;
  onMove: (idx: number, dir: -1 | 1) => void;
  onWithdraw?: (id: number) => void;
}) {
  return (
    <div className={cn(tone === "amber" && "bg-amber-50/40 dark:bg-amber-500/10")}>
      <div className="px-5 pt-3 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
        {title} <span className="text-ink-subtle">({items.length})</span>
      </div>
      <ul className="divide-y divide-line-soft px-2 pb-2">
        {items.map((item, idx) => (
          <QueueRow
            key={item.id}
            item={item}
            index={idx}
            count={items.length}
            departments={departments}
            onPatch={(f) => onPatch(item.id, f)}
            onMove={(dir) => onMove(idx, dir)}
            onWithdrawApproval={() => onWithdraw?.(item.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function QueueRow({
  item,
  index,
  count,
  departments,
  onPatch,
  onMove,
  onWithdrawApproval,
}: {
  item: SupportQueueItem;
  index: number;
  count: number;
  departments: string[];
  onPatch: (fields: Partial<SupportQueueItem>) => void;
  onMove: (dir: -1 | 1) => void;
  onWithdrawApproval?: () => void;
}) {
  const [editingJira, setEditingJira] = useState(false);
  const [jiraDraft, setJiraDraft] = useState(item.jira_key ?? "");
  const inputCls =
    "rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink-2 focus:border-line-strong focus:outline-none";

  const commitJira = () => {
    const v = jiraDraft.trim().toUpperCase();
    if (v !== (item.jira_key ?? "")) onPatch({ jira_key: (v || null) as any });
    setEditingJira(false);
  };

  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <span className="w-5 shrink-0 text-right font-mono text-[11px] font-semibold text-ink-subtle">
        {index + 1}.
      </span>
      <a
        href={`https://${FRESHSERVICE_PORTAL}/a/tickets/${item.fs_ticket_id}`}
        target="_blank"
        rel="noreferrer"
        className="shrink-0 inline-flex items-center gap-1 font-mono text-[11px] text-sky-700 dark:text-sky-300 hover:underline"
      >
        #{item.fs_ticket_id}
        <ExternalLink className="h-2.5 w-2.5" />
      </a>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink">{item.subject}</div>
        <div className="truncate text-[10px] text-ink-subtle">
          {item.requester ? `Submitted by ${item.requester}` : "Submitter unknown"}
          {item.requested_for && ` · for ${item.requested_for}`}
          {item.fs_department && ` · from ${item.fs_department}`}
          {item.fs_created_at && ` · ${new Date(item.fs_created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
        </div>
      </div>

      <AgeChip createdAt={item.fs_created_at} />
      {approvalBadge(item)}
      {item.approval_override === "approved" && onWithdrawApproval && (
        <button
          onClick={onWithdrawApproval}
          className="shrink-0 text-[10px] text-ink-subtle hover:text-ink-3 hover:underline"
          title="Withdraw the planner approval — the item returns to Not approved yet"
        >
          undo
        </button>
      )}

      {/* Jira card link + live status */}
      <div className="shrink-0 flex items-center gap-1.5">
        {item.jira_key && !editingJira ? (
          <>
            <span className="font-mono text-[11px] text-ink-3">{item.jira_key}</span>
            {jiraPill(item.jira_status_category, item.jira_status) ?? (
              <span className="text-[10px] text-ink-subtle">not synced</span>
            )}
            <button
              onClick={() => { setJiraDraft(item.jira_key ?? ""); setEditingJira(true); }}
              className="text-[10px] text-ink-subtle hover:text-ink-3 hover:underline"
            >
              edit
            </button>
          </>
        ) : editingJira ? (
          <input
            autoFocus
            className={cn(inputCls, "w-24 font-mono")}
            placeholder="SSE-123"
            value={jiraDraft}
            onChange={(e) => setJiraDraft(e.target.value)}
            onBlur={commitJira}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitJira();
              if (e.key === "Escape") setEditingJira(false);
            }}
          />
        ) : (
          <button
            onClick={() => setEditingJira(true)}
            className="text-[10px] font-medium text-ink-subtle hover:text-ink-3 hover:underline"
          >
            + Jira
          </button>
        )}
      </div>

      {/* Department picker — the human triage step. The placeholder says
          what picking does: routes the item to that department's page. */}
      <select
        className={cn(inputCls, "shrink-0", !item.department && "border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10")}
        value={item.department ?? ""}
        onChange={(e) => onPatch({ department: (e.target.value || null) as any })}
        title={item.department
          ? "Reassign to a different department"
          : "Route to a department — the request moves to that department's queue"}
      >
        <option value="">{item.department ? "Department…" : "Route to…"}</option>
        {departments.map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>

      {/* Rank + done */}
      <div className="shrink-0 flex items-center gap-1">
        <button onClick={() => onMove(-1)} disabled={index === 0}
          className="rounded p-0.5 text-ink-faint hover:text-ink-3 disabled:opacity-30">
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => onMove(1)} disabled={index === count - 1}
          className="rounded p-0.5 text-ink-faint hover:text-ink-3 disabled:opacity-30">
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <PromoteButton item={item} />
        <button
          onClick={() => onPatch({ done_manual: true })}
          title="Mark done (items with a Jira card complete automatically when the card does)"
          className="ml-1 inline-flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[10px] font-semibold text-ink-muted hover:border-emerald-300 hover:text-emerald-700 dark:hover:text-emerald-300"
        >
          <Check className="h-3 w-3" /> Done
        </button>
      </div>
    </li>
  );
}

/** Enhancement → project (2026-09-29): some enhancements turn out to be
 *  top priorities. Creates a Requested project in New / unscoped on the
 *  board and closes this item as promoted. */
function PromoteButton({ item }: { item: SupportQueueItem }) {
  const promote = usePromoteSupportQueueItem();
  if (item.promoted_project_id) {
    return <Link to={`/portfolio/${item.promoted_project_id}`} className="ml-1 text-[10px] font-semibold text-brand-navy-600 dark:text-brand-navy-300 hover:underline" data-testid="promoted-link">→ {item.promoted_project_id}</Link>;
  }
  return (
    <button
      onClick={() => promote.mutate(item.id, {
        onSuccess: (d) => toast.success(`Promoted to ${d.project_id} — now in New / unscoped on Top Projects`),
        onError: (e: any) => toast.error(e?.response?.data?.detail ?? "Couldn't promote"),
      })}
      disabled={promote.isPending}
      className="ml-1 inline-flex items-center rounded-md border border-line px-2 py-0.5 text-[10px] font-semibold text-ink-muted hover:border-brand-navy-300 hover:text-brand-navy-700 dark:hover:text-brand-navy-300 disabled:opacity-50"
      title="This is really a project — create it on the board (New / unscoped) and close this item"
      data-testid="promote-to-project"
    >
      Promote to project
    </button>
  );
}
